import {Injectable} from '@angular/core';
import {ApiService} from "../global/api.service";
import {Product, Recipe, RecipePrice, RecipeProduct} from "../../types/models";
import {RecipeProductAmountPipe} from '../../pipes/recipe-product-amount.pipe';
import {ProductService} from './product.service';
import {KeyMap} from "../../types/interfaces";
import {ProductChange} from "../../types/io";
import {CartProductItemStorage, CartProductStorage} from "../../types/storage";

@Injectable({
  providedIn: 'root'
})
export class RecipeService {

  cachedRecipeServingsMap: { [recipeId: string]: number } = {};

  constructor(
    private api: ApiService,
    private recipeProductAmountPipe: RecipeProductAmountPipe,
    private productService: ProductService
  ) {
  }

  public async getRecipeSteps(recipeId: string, numPersons?: number): Promise<string[]> {
    return this.api.get(`recipe/steps/${recipeId}${numPersons ? '/' + numPersons : ''}`);
  }

  public async getByIdList(recipeIds: string[]): Promise<Recipe[]> {
    const recipes = await this.api.post('recipe/by-ids', { Recipes: recipeIds });
    (recipes || []).forEach(recipe => this.setPrice(recipe));
    return recipes;
  }

  public async getSuggestions(recipeId: string): Promise<Product[]> {
    return this.api.get(`recipe/suggestions/${recipeId}`);
  }

  public saveSelectedServings(recipe: Recipe, num: number) {
    this.cachedRecipeServingsMap[recipe.Id] = num;
  }

  public getSelectedServings(recipe: Recipe): number {
    return this.cachedRecipeServingsMap[recipe?.Id];
  }

  public async get(id: string): Promise<Recipe> {
    const recipe = await this.api.get(`recipe/${id}`);
    if (recipe) {
      recipe.Favorite = recipe.Favorite || { RecipeId: id, IsUserFavorite: false};
    }
    return recipe;
  }

  public getProductList(recipe: Recipe, numPersons: number): string[] {
    const products: string[] = [];
    for (const recipeProduct of recipe.RecipeProducts) {
      const amountAndUnit = this.recipeProductAmountPipe.transform(recipeProduct, recipe.NumPersons, numPersons, false);
      products.push(amountAndUnit + ' ' + recipeProduct.Product.Name);
    }
    return products;
  }

  public async getAllByProduct(productId: string): Promise<Recipe[]> {
    const recipes = await this.api.get(`recipe/by-product/${productId}`);
    (recipes || []).forEach(recipe => this.setPrice(recipe));
    const inStockRecipes = [];
    const outOfStockRecipes = [];
    for (const recipe of recipes) {
      if (this.recipeInStock(recipe)) {
        inStockRecipes.push(recipe);
      } else {
        outOfStockRecipes.push(recipe);
      }
    }
    return inStockRecipes.concat(outOfStockRecipes);
  }

  public getAmountNeeded(recipeProduct: RecipeProduct, recipeServings: number, selectedServings: number): number {
    const product = recipeProduct.Product;
    if (!product || !recipeProduct.Amount) {
      return null;
    }
    const baseProduct = recipeProduct.BaseProduct || product;
    const mean = ProductService.getMeanAmounts(baseProduct);
    if (mean) {
      const baseMeanAmount = mean.Piece;
      const recipeProductAmountPerServing = (recipeProduct.Amount * baseMeanAmount) / recipeServings
      return selectedServings * recipeProductAmountPerServing;
    } else {
      const recipeProductAmountPerServing = recipeProduct.Amount / recipeServings;
      return selectedServings * recipeProductAmountPerServing;
    }
  }

  public getUnitsNeededByAmount(product: Product, amount: number): number {
    if (!product || !amount) {
      return 0;
    }
    const productAmount = ProductService.getMeanAmounts(product)?.Total || product.Amount;
    if (!productAmount) {
      return 0;
    }
    return Math.max(Math.ceil(amount / productAmount), 1);
  }

  public getUnitsNeeded(recipeProduct: RecipeProduct, product: Product, recipeNumPersons: number, actualNumPersons: number): number {
    const amount = this.getAmountNeeded(recipeProduct, recipeNumPersons, actualNumPersons);
    if (!amount) {
      return 0;
    }
    return this.getUnitsNeededByAmount(product, amount);
  }

  public getFirstRecipeStep(recipe: Recipe, NumPersons?: number) {
    const numPersons = recipe.NumPersons;//NumPersons || recipe.NumPersons;
    const productList = this.getProductList(recipe, numPersons);
    let productListText = 'Du benötigst für ';
    if (numPersons === 1) {
      productListText += 'eine Person:<br>';
    } else {
      productListText += numPersons + ' Personen:<br>';
    }
    productListText += productList.join('<br>');
    return productListText;
  }

  private getRecipeProduct(recipeProduct: RecipeProduct): Product {
    if (this.productService.getProductInStock(recipeProduct.Product)) {
      return recipeProduct.Product;
    }
    return (recipeProduct.Products || []).find(product => this.productService.getProductInStock(product));
  }

  public getRecipePrice(recipe: Recipe, servings?: number, selection?: KeyMap<ProductChange>): RecipePrice {
    if (!recipe) {
      return {
        Products: 0,
        Deposit: 0,
        Sum: 0
      };
    }
    const productSelection = selection || {};
    let productsSum = 0;
    let depositSum = 0;
    for (const recipeProduct of recipe.RecipeProducts || []) {
      if (!recipeProduct.Required) {
        continue;
      }
      const selectedProduct = productSelection[recipeProduct.Id]?.SelectedProduct;
      const product = selectedProduct?.Product || this.getRecipeProduct(recipeProduct);
      if (!product) {
        return {
          Products: 0,
          Deposit: 0,
          Sum: 0
        };
      }
      const amount = this.getUnitsNeeded(recipeProduct, product, recipe.NumPersons, servings || recipe.NumPersons);
      productsSum += (amount || 0) * (product.PriceGross || 0);
      depositSum += (amount || 0) * (product.Deposit || 0);
    }
    return {
      Products: productsSum,
      Deposit: depositSum,
      Sum: productsSum + depositSum
    };
  }

  public getRecipeCart(recipe: Recipe, servings?: number, selection?: KeyMap<ProductChange>): CartProductStorage {
    const storage: CartProductStorage = {};
    if (!recipe) {
      return storage;
    }
    const productSelection = selection || {};
    for (const recipeProduct of recipe.RecipeProducts || []) {
      if (!recipeProduct.Required) {
        continue;
      }
      const selectedProduct = productSelection[recipeProduct.Id]?.SelectedProduct;
      const product = selectedProduct?.Product || this.getRecipeProduct(recipeProduct);
      if (!product) {
        return {};
      }
      const amount = this.getUnitsNeeded(recipeProduct, product, recipe.NumPersons, servings || recipe.NumPersons);
      if (amount) {
        const cartProduct: CartProductItemStorage = storage[product.Id] || { ProductId: product.Id, Amount: 0 };
        cartProduct.Amount += amount;
        storage[product.Id] = cartProduct;
      }
    }
    return storage;
  }

  public setPrice(recipe: Recipe) {
      recipe.RecipePrice = this.getRecipePrice(recipe, recipe.NumPersons);
  }

  public recipeInStock(recipe: Recipe, selection?: KeyMap<ProductChange>): boolean {
    if (!recipe || recipe.Draft) {
      return false;
    }
    let inStock = true;
    let hasRequiredProduct = false;
    const productSelection = selection || {};
    for (const recipeProduct of recipe.RecipeProducts || []) {
      if (!recipeProduct.Required) {
        continue;
      }
      hasRequiredProduct = true;
      const selectedProduct = productSelection[recipeProduct.Id]?.SelectedProduct;
      const product = selectedProduct?.Product || this.getRecipeProduct(recipeProduct);
      if (!product) {
        inStock = false;
        break;
      }
    }
    return inStock && hasRequiredProduct;
  }

  public sortRecipesByAvailability(recipes: Recipe[]): Recipe[] {
    if (!recipes) {
      return [];
    }
    const availableRecipes = [];
    const outOfStockRecipes = [];
    for (const recipe of recipes) {
      if (this.recipeInStock(recipe)) {
        availableRecipes.push(recipe);
      } else {
        outOfStockRecipes.push(recipe);
      }
    }
    const sortedRecipes = [];
    if (availableRecipes.length > 0) {
      sortedRecipes.push(...availableRecipes);
    }
    if (outOfStockRecipes.length > 0) {
      sortedRecipes.push(...outOfStockRecipes);
    }
    return sortedRecipes;
  }
}
